// Grouping and ordering for the quota table: each quota's Alchemer and Wrangler rows stay together.
import { safeDecode } from '../utils/decode';

const SYSTEM_ORDER = { alchemer: 0, wrangler: 1 };
const nameKey = (quota) => safeDecode(quota.name || '').trim().toLowerCase();

/**
 * Rows grouped into units: an Alchemer quota with the Wrangler quotas that copy it. A Wrangler
 * quota joins an Alchemer one by alchemer_quota_id (set when it was imported), or else by the
 * same name (partners don't get alchemer_quota_id). Anything unmatched is a unit of its own.
 * Within a unit, Alchemer comes first. Units keep the order of their first row.
 */
export const groupQuotas = (quotas) => {
  const groups = [];
  const byAlchemerId = new Map();
  const byName = new Map();
  for (const quota of quotas) {
    if (quota.system !== 'alchemer') continue;
    const group = [quota];
    groups.push(group);
    byAlchemerId.set(String(quota.id), group);
    if (!byName.has(nameKey(quota))) byName.set(nameKey(quota), group);
  }
  for (const quota of quotas) {
    if (quota.system === 'alchemer') continue;
    const group = (quota.alchemer_quota_id != null && byAlchemerId.get(String(quota.alchemer_quota_id)))
      || byName.get(nameKey(quota));
    if (group) group.push(quota);
    else groups.push([quota]);
  }
  for (const group of groups) group.sort((a, b) => SYSTEM_ORDER[a.system] - SYSTEM_ORDER[b.system]);
  return groups;
};

export const percent = (quota) => (quota.limit > 0 ? (quota.responses / quota.limit) * 100 : null);

/** 'progress', 'full' (exactly at the limit) or 'over' (above it). */
export const progressState = (quota) => {
  if (!(quota.limit > 0)) return 'progress';
  if (quota.responses > quota.limit) return 'over';
  if (quota.responses === quota.limit) return 'full';
  return 'progress';
};

const sortValue = (quota, orderBy) => {
  if (orderBy === 'progress') return percent(quota) ?? -1;
  if (orderBy === 'name') return nameKey(quota);
  if (orderBy === 'description') return (quota.description || '').toLowerCase();
  if (orderBy === 'system') return SYSTEM_ORDER[quota.system] ?? 9;
  const value = quota[orderBy];
  return value == null ? -1 : value;
};

/** Units sorted by their first row's value, so a pair moves as one. */
export const sortGroups = (groups, orderBy, order) => {
  const direction = order === 'desc' ? -1 : 1;
  return [...groups].sort((a, b) => {
    const va = sortValue(a[0], orderBy);
    const vb = sortValue(b[0], orderBy);
    if (typeof va === 'string' || typeof vb === 'string') {
      return direction * String(va).localeCompare(String(vb), undefined, { numeric: true });
    }
    return direction * (va - vb);
  });
};
